/* ------------------------------------------------------------------
   zetse — the search sheet behind the header search box.

   Tap the box and the whole screen becomes the search, the way it does
   in the quick-commerce apps.

   Idle (nothing typed): the last few searches as tappable cards, two to
   a row, then "Top picks for you" — the Best Sellers collection, three
   to a row — so there is always something to tap.

   Typing: a short list of suggestions — the classes a book is stocked
   for when the typed words name an author, a publisher or an NCERT
   title ("RD Sharma for Class 6", "… for Class 7"), then real
   collections, then phrases built out of what the shop actually stocks
   ("Pencil box", "Apsara pencil") — with the matching products three to
   a row underneath.

   Three things happen quietly underneath:

     • a word the shop has never used is re-run with the spelling
       fixed, and the fixed wording wins only if it genuinely finds
       more;
     • asking for a class the shop does not stock says so — "no Class
       11 of S.L. Arora, showing Class 12" — instead of handing over
       the wrong year's book without comment;
     • opening a product from the sheet leaves a note behind, so the
       phone's back button lands on the search results rather than the
       home page.

   The numbers worth knowing about:
     MAX_SUGGEST      suggestion rows before the products start
     MAX_COLLECTIONS  how many of those rows real collections may take
     MAX_CLASSES      class rows, which sit above and outside the cap
     MAX_PRODUCTS     products in the grid — a multiple of 3 keeps the
                      last row full on a phone
     MAX_RECENT       past searches kept on the customer's own device
------------------------------------------------------------------- */
(function () {
  'use strict';

  var form  = document.getElementById('HeaderSearchForm');
  var input = document.getElementById('HeaderSearchInput');
  var panel = document.getElementById('HeaderSearchResults');
  if (!form || !input || !panel) return;

  /* The older, plainer predictive dropdown still ships inside theme.js.
     It finds its panel by this id and quietly stands down when the id is
     gone, so renaming it here — before theme.js runs — leaves exactly one
     script writing into the sheet instead of two fighting over it. */
  panel.id = 'HeaderSearchPanel';
  input.setAttribute('aria-controls', 'HeaderSearchPanel');

  var MAX_SUGGEST     = 5;
  var MAX_COLLECTIONS = 4;
  var MAX_CLASSES     = 8;
  var MAX_PRODUCTS    = 12;
  var MAX_RECENT      = 6;
  var DEBOUNCE        = 170;
  var RECENT_KEY      = 'zetse:recent-searches';
  var BACK_KEY        = 'zetse:back-to-search';

  /* Honour a locale prefix (/en-in/search) rather than hard-coding /search. */
  var SEARCH_URL  = form.getAttribute('action') || '/search';
  var ROOT        = SEARCH_URL.replace(/\/search\/?$/, '');
  var SUGGEST_URL = ROOT + '/search/suggest.json';

  var debounceTimer = null;
  var activeQuery = '';
  var lastFocus = null;
  var isOpen = false;
  var pushedState = false;

  /* Set when a fixed spelling beat what was typed. */
  var correction = null;

  /* A picture for the recent-search card: whatever the top result was
     the last time that word was searched in this tab. */
  var thumbForQuery = {};

  function smart() { return window.ZetseSmart || null; }

  /* ---------- small helpers ---------- */

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(amount) {
    var n = parseFloat(amount || 0);
    if (!isFinite(n)) n = 0;
    return '₹' + Math.round(n).toLocaleString('en-IN');
  }

  function img(image) {
    if (!image) return '';
    return typeof image === 'string' ? image : (image.url || image.src || '');
  }

  function productImage(p) { return img(p.image || p.featured_image); }

  function searchUrlFor(term) {
    return SEARCH_URL + '?type=product&q=' + encodeURIComponent(term);
  }

  function here() { return location.pathname + location.search; }

  function listClasses(nums) {
    var out = nums.map(function (n) { return 'Class ' + n; });
    if (out.length === 1) return out[0];
    return out.slice(0, -1).join(', ') + ' and ' + out[out.length - 1];
  }

  var MAGNIFIER = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>';

  /* The typed part stays light, the rest goes bold, so the eye lands on
     what the suggestion is adding to what was typed. */
  function highlight(label, query) {
    var l = String(label);
    var i = l.toLowerCase().indexOf(String(query).toLowerCase());
    if (i === -1) return '<b>' + esc(l) + '</b>';
    var pre  = l.slice(0, i);
    var mid  = l.slice(i, i + query.length);
    var post = l.slice(i + query.length);
    return (pre ? '<b>' + esc(pre) + '</b>' : '') +
           '<span>' + esc(mid) + '</span>' +
           (post ? '<b>' + esc(post) + '</b>' : '');
  }

  /* One product card, used by both grids. */
  function cardHTML(p, term) {
    var src = productImage(p);
    var compare = p.compare_at_price_max || p.compare_at_price;
    var hasCompare = compare && parseFloat(compare) > parseFloat(p.price);
    var off = hasCompare ? Math.round((parseFloat(compare) - parseFloat(p.price)) / parseFloat(compare) * 100) : 0;
    return '<a class="sp-card" href="' + esc(p.url) + '"' + (term ? ' data-sp-term="' + esc(term) + '"' : '') + '>' +
      '<span class="sp-card__img">' +
      (off > 0 ? '<span class="sp-card__off">' + off + '% OFF</span>' : '') +
      (src ? '<img src="' + esc(src) + '" alt="" loading="lazy">' : '') +
      '</span>' +
      '<span class="sp-card__title">' + esc(p.title) + '</span>' +
      '<span class="sp-card__price">' + money(p.price) +
      (hasCompare ? '<s>' + money(compare) + '</s>' : '') + '</span>' +
      '</a>';
  }

  /* ---------- back from a product lands on the results ---------- */

  function markBack(term) {
    term = String(term || '').trim();
    if (!term) return;
    try {
      sessionStorage.setItem(BACK_KEY, JSON.stringify({ u: searchUrlFor(term), p: here() }));
    } catch (e) {}
  }

  function restoreToSearch() {
    var raw = null;
    try { raw = sessionStorage.getItem(BACK_KEY); } catch (e) { return false; }
    if (!raw) return false;
    var note = null;
    try { note = JSON.parse(raw); } catch (e) { note = null; }
    if (!note || note.p !== here()) return false;
    try { sessionStorage.removeItem(BACK_KEY); } catch (e) {}
    if (!note.u || note.u === here()) return false;
    location.replace(note.u);
    return true;
  }

  window.addEventListener('pageshow', function (e) {
    var nav = (window.performance && performance.getEntriesByType &&
               performance.getEntriesByType('navigation')[0]) || null;
    if (e.persisted || (nav && nav.type === 'back_forward')) restoreToSearch();
  });

  /* ---------- opening and closing ---------- */

  function openSheet() {
    if (isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;
    document.body.classList.add('search-predictive-open');
    input.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    /* So the phone's back gesture closes the sheet instead of leaving
       the page — the same thing the back arrow in the box does. */
    try { history.pushState({ zetseSearch: 1 }, '', location.href); pushedState = true; } catch (e) { pushedState = false; }
  }

  function closeSheet(fromHistory) {
    if (!isOpen) return;
    isOpen = false;
    document.body.classList.remove('search-predictive-open');
    input.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    if (!fromHistory && pushedState) {
      pushedState = false;
      try { history.back(); } catch (e) {}
    }
    pushedState = false;
    if (lastFocus && lastFocus !== input && lastFocus.focus) lastFocus.focus();
  }

  window.addEventListener('popstate', function () {
    if (restoreToSearch()) return;
    if (isOpen) closeSheet(true);
  });

  /* ---------- idle: recent searches + top picks ---------- */

  function readRecent() {
    try {
      var raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw.map(function (e) {
        /* Earlier versions stored plain strings. */
        if (typeof e === 'string') return { t: e, i: '' };
        if (e && typeof e.t === 'string') return { t: e.t, i: typeof e.i === 'string' ? e.i : '' };
        return null;
      }).filter(function (e) { return e && e.t.trim(); }).slice(0, MAX_RECENT);
    } catch (e) { return []; }
  }

  function writeRecent(list) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT))); } catch (e) {}
  }

  function remember(term) {
    term = String(term || '').trim();
    if (!term) return;
    var key = term.toLowerCase();
    var list = readRecent();
    var thumb = thumbForQuery[key] || '';
    list.forEach(function (e) { if (e.t.toLowerCase() === key && !thumb) thumb = e.i; });
    list = list.filter(function (e) { return e.t.toLowerCase() !== key; });
    list.unshift({ t: term, i: thumb });
    writeRecent(list);
  }

  /* Rendered into the page by snippets/search-top-picks.liquid, so the
     sheet has something to show without waiting on a request. */
  function topPicks() {
    var el = document.getElementById('SearchTopPicks');
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent);
      return (data && data.products && data.products.length) ? data : null;
    } catch (e) { return null; }
  }

  function renderIdle() {
    var list = readRecent();
    var picks = topPicks();
    var html = '<div class="sp">';

    if (list.length) {
      html += '<section class="sp-sec sp-sec--big">' +
        '<div class="sp-sec__head"><h2>Recent searches</h2>' +
        '<button type="button" class="sp-clear" data-sp-clear>clear</button></div>' +
        '<ul class="sp-recent">';
      list.forEach(function (e) {
        html += '<li><a class="sp-recent__chip" href="' + esc(searchUrlFor(e.t)) + '" data-sp-recent="' + esc(e.t) + '">' +
          '<span class="sp-recent__thumb">' +
          (e.i ? '<img src="' + esc(e.i) + '" alt="" loading="lazy">' : MAGNIFIER) +
          '</span><span class="sp-recent__txt">' + esc(e.t) + '</span></a></li>';
      });
      html += '</ul></section>';
    }

    if (picks) {
      html += '<section class="sp-sec sp-sec--big">' +
        '<div class="sp-sec__head"><h2>Top picks for you</h2>' +
        (picks.url ? '<a class="sp-sec__link" href="' + esc(picks.url) + '">View all</a>' : '') +
        '</div><div class="sp-grid">';
      picks.products.slice(0, 9).forEach(function (p) { html += cardHTML(p, ''); });
      html += '</div></section>';
    }

    if (!list.length && !picks) {
      html += '<div class="sp-welcome"><strong>Search zetse</strong>' +
        '<span>Books, stationery, novels, school supplies — delivered in 30 minutes.</span></div>';
    }

    panel.innerHTML = html + '</div>';
    panel.scrollTop = 0;
  }

  function renderLoading() {
    panel.innerHTML = '<div class="sp"><div class="sp-note">Searching…</div></div>';
  }

  function renderEmpty(query) {
    panel.innerHTML = '<div class="sp"><div class="sp-note sp-note--empty">' +
      '<strong>No matches for “' + esc(query) + '”</strong>' +
      '<span>Try the subject, the class, or just the author’s name.</span>' +
      '</div></div>';
  }

  /* ---------- turning the API response into suggestion rows ---------- */

  function thumbFor(label, products) {
    var words = String(label).toLowerCase().split(/\s+/).filter(function (w) { return w.length > 2; });
    for (var i = 0; i < products.length; i++) {
      var hay = ((products[i].title || '') + ' ' + (products[i].vendor || '') + ' ' + (products[i].product_type || '')).toLowerCase();
      var all = true;
      for (var j = 0; j < words.length; j++) { if (hay.indexOf(words[j]) === -1) { all = false; break; } }
      if (all && productImage(products[i])) return productImage(products[i]);
    }
    return '';
  }

  /* A class row wants that class's own cover, not the series' first
     book six times over — and "Class 1" must not settle for Class 10. */
  function thumbForClass(base, c, products) {
    var re = new RegExp('class[^0-9a-z]*' + c + '(?![0-9])');
    var words = String(base).toLowerCase().split(/\s+/).filter(function (w) { return w.length > 2; });
    for (var i = 0; i < products.length; i++) {
      var title = (products[i].title || '').toLowerCase();
      if (!re.test(title)) continue;
      var hay = title + ' ' + (products[i].vendor || '').toLowerCase();
      var all = true;
      for (var j = 0; j < words.length; j++) { if (hay.indexOf(words[j]) === -1) { all = false; break; } }
      if (all && productImage(products[i])) return productImage(products[i]);
    }
    return thumbFor(base, products);
  }

  function byCountDesc(counts) {
    return Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; });
  }

  function buildSuggestions(query, collections, queries, products, note) {
    var q = query.toLowerCase();
    var rows = [], seen = {};
    var fallbackIndex = 0;
    var S = smart();

    /* 0. One title, many classes. "RD Sharma" is six different books, so
          asking which class is more use than six near-identical covers.
          When the customer already named a class the shop does not
          stock, the same rows become the way out of it. These sit above
          the cap rather than inside it — they are the answer to the
          question, not a suggestion alongside it. */
    var base = S ? S.stripClass(query) : query;
    var classes = note ? note.available : (S ? S.classesFor(query, products) : []);
    if (base && classes.length) {
      classes.slice(0, MAX_CLASSES).forEach(function (c) {
        var label = base + ' for Class ' + c;
        seen[label.toLowerCase()] = true;
        rows.push({
          label: label,
          url: searchUrlFor(base + ' class ' + c),
          thumb: thumbForClass(base, c, products),
          kind: 'class',
          tag: 'Class ' + c
        });
      });
    }
    var reserved = rows.length;

    function add(label, url, thumb, kind) {
      label = String(label || '').trim();
      if (!label) return;
      var key = label.toLowerCase();
      if (seen[key] || rows.length - reserved >= MAX_SUGGEST) return;
      seen[key] = true;
      if (!thumb) {
        var p = products[fallbackIndex++ % Math.max(products.length, 1)];
        thumb = p ? productImage(p) : '';
      }
      rows.push({ label: label, url: url, thumb: thumb, kind: kind });
    }

    /* 1. Real collections — a customer tapping these lands on a proper
          category page rather than a search result. */
    function leafOf(title) { return String(title).split('>').pop().trim() || String(title); }
    var leaves = {};
    collections.forEach(function (c) {
      var k = leafOf(c.title).toLowerCase();
      leaves[k] = (leaves[k] || 0) + 1;
    });
    collections.slice(0, MAX_COLLECTIONS).forEach(function (c) {
      var leaf = leafOf(c.title);
      var label = leaf;
      if (leaves[leaf.toLowerCase()] > 1) {
        label = String(c.title).split('>').map(function (s) { return s.trim(); }).slice(-2).join(' > ');
      }
      add(label, c.url, img(c.image || c.featured_image) || thumbFor(c.title, products), 'collection');
    });

    /* 2. Shopify's own popular-search suggestions, where the shop has
          enough search history for them to exist. */
    queries.forEach(function (s) {
      var text = s.text || s.styled_text || '';
      add(text, s.url || searchUrlFor(text), thumbFor(text, products), 'query');
    });

    /* 3. Product types that contain what was typed — this is what turns
          "pencil" into "Pencil box" and "Pencil pouch". */
    var types = {};
    products.forEach(function (p) {
      var t = String(p.product_type || '').trim();
      if (t && t.toLowerCase().indexOf(q) !== -1 && t.toLowerCase() !== q) types[t] = (types[t] || 0) + 1;
    });
    byCountDesc(types).forEach(function (t) { add(t, searchUrlFor(t), thumbFor(t, products), 'type'); });

    /* 4. The brands that keep coming back in the results, pinned in front
          of the search itself — "Apsara pencil", "Classmate notebook". */
    var vendors = {};
    products.forEach(function (p) {
      var v = String(p.vendor || '').trim();
      if (v && v.toLowerCase().indexOf(q) === -1) vendors[v] = (vendors[v] || 0) + 1;
    });
    byCountDesc(vendors).slice(0, 0).forEach(function (v) {
      var label = v + ' ' + query;
      add(label, searchUrlFor(label), thumbFor(label, products), 'vendor');
    });

    return rows;
  }

  function renderResults(query, rows, products, typed, note) {
    if (!rows.length && !products.length) { renderEmpty(query); return; }
    var html = '<div class="sp">';

    if (typed) {
      html += '<div class="sp-fix">Showing results for <b>' + esc(query) + '</b>' +
        '<a href="' + esc(searchUrlFor(typed)) + '" data-sp-keep="' + esc(typed) + '">Search for “' + esc(typed) + '” instead</a></div>';
    }

    if (note) {
      html += '<div class="sp-fix sp-fix--class">' +
        'No <b>Class ' + note.asked + '</b> of “' + esc(note.base) + '” right now — showing <b>' +
        esc(listClasses(note.available)) + '</b> instead.</div>';
    }

    if (rows.length) {
      html += '<section class="sp-sec"><div class="sp-sug">';
      rows.forEach(function (r) {
        html += '<a class="sp-sug__row" href="' + esc(r.url) + '" data-sp-term="' + esc(r.label) + '">' +
          '<span class="sp-sug__thumb">' +
          (r.thumb ? '<img src="' + esc(r.thumb) + '" alt="" loading="lazy">' : MAGNIFIER) +
          '</span>' +
          '<span class="sp-sug__text">' + highlight(r.label, query) + '</span>' +
          (r.kind === 'collection' ? '<span class="sp-sug__tag">Collection</span>' : '') +
          (r.kind === 'class' ? '<span class="sp-sug__tag">' + esc(r.tag) + '</span>' : '') +
          '</a>';
      });
      html += '</div></section>';
    }

    /* Three to a row, so a row with one lonely product in it never shows. */
    if (products.length > 3) products = products.slice(0, Math.floor(Math.min(products.length, MAX_PRODUCTS) / 3) * 3);

    if (products.length) {
      html += '<section class="sp-sec sp-sec--products">' +
        '<div class="sp-sec__head"><h2 class="sp-sec__label">Products</h2>' +
        '<a class="sp-sec__link" href="' + esc(searchUrlFor(query)) + '" data-sp-term="' + esc(query) + '">View all</a></div>' +
        '<div class="sp-grid">';
      products.forEach(function (p) { html += cardHTML(p, query); });
      html += '</div></section>';
    }

    panel.innerHTML = html + '</div>';
    panel.scrollTop = 0;
  }

  /* ---------- the search itself ---------- */

  function suggestUrl(query) {
    return SUGGEST_URL +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=product,collection,query' +
      '&resources[limit]=10' +
      /* Without this the ten is shared out between products, collections and
         query suggestions, and the grid ends up with three products in it. */
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,vendor,tag';
  }

  function ask(query) {
    return fetch(suggestUrl(query)).then(function (r) {
      if (!r.ok) throw new Error('search failed');
      return r.json();
    }).then(function (data) {
      var results = (data.resources && data.resources.results) || {};
      return {
        collections: results.collections || [],
        queries: results.queries || [],
        products: results.products || []
      };
    });
  }

  function show(query, res, typed) {
    if (res.products.length) thumbForQuery[query.toLowerCase()] = productImage(res.products[0]);
    var S = smart();
    var note = S ? S.classNote(query, res.products) : null;
    renderResults(query, buildSuggestions(query, res.collections, res.queries, res.products, note), res.products, typed, note); classThumbs(S ? S.stripClass(query) : query);

    /* Each "for Class N" row gets that class's own cover: the first ten
       results rarely cover every class, so ask for each class separately. */
    function classThumbs(base) {
      var CT = window.__zClassThumbs || (window.__zClassThumbs = {});
      var ROM = ['', 'i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii'];
      [].forEach.call(panel.querySelectorAll('.sp-sug__row'), function (row) {
        var tag = row.querySelector('.sp-sug__tag');
        var m = tag && tag.textContent.match(/^Class (\d+)$/);
        if (!m) return;
        var c = +m[1];
        var re = new RegExp('class[^0-9a-z]*(' + c + '|' + ROM[c] + ')(?![0-9a-z])', 'i');
        var key = base.toLowerCase() + '|' + c;
        var done = function (src) {
          var box = row.querySelector('.sp-sug__thumb');
          if (src && box) box.innerHTML = '<img src="' + esc(src) + '" alt="" loading="lazy">';
        };
        if (key in CT) { done(CT[key]); return; }
        ask(base + ' class ' + c).then(function (r) {
          var hit = r.products.filter(function (p) { return re.test(p.title || '') && productImage(p); })[0];
          CT[key] = hit ? productImage(hit) : '';
          done(CT[key]);
        }).catch(function () {});
      });
    }
  }

  function runSearch(query, keepAsTyped) {
    activeQuery = query;
    correction = null;
    renderLoading();

    ask(query).then(function (res) {
      if (activeQuery !== query) return;

      /* Only a word the shop has never used is worth second-guessing,
         and even then the fixed spelling has to earn it by finding more
         than what was typed did — Shopify's own matching is loose
         enough to return *something* for almost anything. */
      var S = smart();
      var fixed = (!keepAsTyped && S) ? S.correct(query) : { changed: false };
      if (!fixed.changed) { show(query, res, null); return; }

      ask(fixed.q).then(function (res2) {
        if (activeQuery !== query) return;
        if (res2.products.length > res.products.length) {
          correction = { from: query, to: fixed.q };
          show(fixed.q, res2, query);
        } else {
          show(query, res, null);
        }
      }).catch(function () {
        if (activeQuery === query) show(query, res, null);
      });
    }).catch(function () {
      if (activeQuery === query) renderEmpty(query);
    });
  }

  function currentTerm() {
    return (correction && correction.to) || activeQuery || '';
  }

  /* ---------- wiring ---------- */

  var back = document.createElement('button');
  back.type = 'button';
  back.className = 'search-predictive__back';
  back.setAttribute('aria-label', 'Close search');
  back.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
  form.insertBefore(back, form.firstChild);
  back.addEventListener('click', function () { closeSheet(); input.blur(); });

  input.addEventListener('focus', function () {
    openSheet();
    if (!input.value.trim()) renderIdle();
  });

  input.addEventListener('input', function () {
    var query = input.value.trim();
    openSheet();
    clearTimeout(debounceTimer);
    if (!query) { activeQuery = ''; correction = null; renderIdle(); return; }
    debounceTimer = setTimeout(function () { runSearch(query); }, DEBOUNCE);
  });

  input.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { e.preventDefault(); closeSheet(); input.blur(); }
  });

  form.addEventListener('submit', function (e) {
    var query = input.value.trim();
    if (!query) { e.preventDefault(); return; }
    if (correction && correction.from === query && correction.to) {
      input.value = correction.to;
      query = correction.to;
    }
    remember(query);
  });

  panel.addEventListener('click', function (e) {
    var clear = e.target.closest('[data-sp-clear]');
    if (clear) { e.preventDefault(); writeRecent([]); renderIdle(); return; }

    /* "No, I meant what I typed." Re-runs in the sheet with the speller
       stood down for that query. */
    var keep = e.target.closest('[data-sp-keep]');
    if (keep) {
      e.preventDefault();
      var typed = keep.getAttribute('data-sp-keep');
      input.value = typed;
      input.focus();
      runSearch(typed, true);
      return;
    }

    /* A past search re-runs in the sheet instead of costing a page load. */
    var recent = e.target.closest('[data-sp-recent]');
    if (recent) {
      e.preventDefault();
      var term = recent.getAttribute('data-sp-recent');
      input.value = term;
      remember(term);
      input.focus();
      runSearch(term);
      return;
    }

    var link = e.target.closest('[data-sp-term]');
    if (link) {
      remember(link.getAttribute('data-sp-term'));
      markBack(currentTerm());
    }
  });

  /* Anything that reached a results page — the search page's own box, a
     shared link — counts as a search worth remembering. */
  (function () {
    if (location.pathname.indexOf('/search') === -1) return;
    var m = location.search.match(/[?&]q=([^&]*)/);
    if (m) remember(decodeURIComponent(m[1].replace(/\+/g, ' ')));
  })();
})();
